<script module lang="ts">
  import type { AllowedTeamShow, TeamBadgeData } from './values.types';

  export interface Props {
    team: TeamBadgeData | null | undefined;
    /** Defaults to `team`: acronym and name. */
    show?: AllowedTeamShow;
    /** Scale of the font size, 1 in tables. */
    size?: number;
    /** A tooltip with the full name; defaults to true when only the acronym shows. */
    tooltip?: boolean;
  }
</script>

<script lang="ts">
  import { CommonTexts } from '../../services/CommonTexts';
  import { allowedTeamShows } from './values.types';
  import Tooltip from '../tooltips/Tooltip.svelte';

  const { team, show, size = 1, tooltip }: Props = $props();

  const UseShow = $derived(show && allowedTeamShows.includes(show) ? show : allowedTeamShows[0]);
  const ShowTooltip = $derived(Boolean(team) && (tooltip ?? UseShow === 'acronym'));
</script>

{#snippet badge(data: TeamBadgeData)}
  <!-- like UserBadge, but a pill: a team acronym may be longer than three letters -->
  <span class="teamBadge" style:--badge-size={size}>
    <span class="acronym mono" style:background-color={data.color ?? undefined}>{data.acronym}</span>
    {#if UseShow !== 'acronym'}
      <span class="name">{data.team}</span>
    {/if}
  </span>
{/snippet}

{#if team}
  {#if ShowTooltip}
    <Tooltip placement="top" toggleOnTouch={true} touchTimeout={30}>
      {#snippet content()}
        <span class="tooltipValue">{team.team}</span>
      {/snippet}
      {@render badge(team)}
    </Tooltip>
  {:else}
    {@render badge(team)}
  {/if}
{:else}
  <span>{CommonTexts.Empty}</span>
{/if}

<style lang="scss">
  @use '../../../styles/mixins';

  .teamBadge {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.35em;
    white-space: nowrap;
    font-size: calc(var(--font-size) * var(--badge-size, 1));
    line-height: 1;
    vertical-align: middle;
    cursor: default;

    @include mixins.greaterMedium {
      gap: 0.5em;
    }

    .acronym {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      min-width: 1.5em;
      height: 1.5em;
      padding: 0 0.45em;
      box-sizing: border-box;
      border-radius: 0.75em;
      background: #1a251e;
      color: #fff;
      font-size: 0.75em;
      font-weight: bold;
      letter-spacing: 0.03em;
      user-select: none;
    }

    .name {
      display: inline-flex;
      align-items: center;
      line-height: 1.2;
    }
  }
</style>
